import React, { useState, useMemo } from "react";
import { ColumnDef, VisibilityState, ColumnFiltersState, SortingState, RowSelectionState, ColumnSizingState } from "@tanstack/react-table";
import { type Device } from "@/hooks/useDevices";
import { getTimeAgo } from "@/utils/deviceStatus";
import { MoreVertical, ExternalLink, Terminal, Gamepad2, RefreshCw, Power, Tags, X, ArrowUpDown } from "lucide-react";
import { AppleDeviceIcon } from "@/components/icons";
import { detectAppleDevice } from "@/utils/deviceModelDetection";
import StatusBadge from "./StatusBadge";
import CustomerTag from "./CustomerTag";
import { Button } from "./ui/button";
import { Checkbox } from "./ui/checkbox";
import { useNavigate } from "react-router-dom";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  DropdownMenuCheckboxItem,
} from "@/components/ui/dropdown-menu";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Input } from "./ui/input";
import { toast } from "sonner";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Loader2 } from "lucide-react";
import { TagAssignmentDialog } from "@/components/tags/TagAssignmentDialog";
import { TagRemovalDialog } from "@/components/tags/TagRemovalDialog";
import { TagsDisplay } from "@/components/tags/TagsDisplay";
import { DataTable } from "@/components/ui/data-table";
import { BulkActionToolbar } from "./device/BulkActionToolbar";
import { ColumnTagFilter } from "@/components/device/ColumnTagFilter";
import { ColumnMultiSelectFilter, FilterOption } from "@/components/device/ColumnMultiSelectFilter";
import { useEffect } from "react";

interface Tag {
  id: string;
  name: string;
  description?: string;
  color: string;
  type: "static" | "dynamic";
}

interface DeviceTableProps {
  devices: Device[];
  deviceTags?: Map<string, any[]>; // Phase 1: accept pre-fetched tags
}

const DeviceTable = ({ devices, deviceTags }: DeviceTableProps) => {
  const navigate = useNavigate();
  const [isDeploying, setIsDeploying] = useState(false);
  const [selectedConfig, setSelectedConfig] = useState<string | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [selectedDeviceId, setSelectedDeviceId] = useState<string | null>(null);
  const [isTagAssignmentOpen, setIsTagAssignmentOpen] = useState(false);
  const [selectedDeviceForTags, setSelectedDeviceForTags] = useState<{ id: string; name: string } | null>(null);
  const [isTagRemovalOpen, setIsTagRemovalOpen] = useState(false);
  const [selectedDeviceForRemoval, setSelectedDeviceForRemoval] = useState<{ id: string; name: string } | null>(null);
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({});
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [sorting, setSorting] = useState<SortingState>([]);
  const [columnSizing, setColumnSizing] = useState<ColumnSizingState>({});
  const [availableTags, setAvailableTags] = useState<Tag[]>([]);
  
  const getDeviceIcon = (device: Device) => {
    const { osType, variant } = detectAppleDevice(
      device.os_type || 'macOS',
      device.name,
      undefined, // modelIdentifier not available yet
      undefined  // formFactor not available yet
    );
    
    return (
      <AppleDeviceIcon 
        osType={osType}
        variant={variant as any}
        modelName={device.name}
        className="h-5 w-5 text-muted-foreground"
      />
    );
  };

  const getDeviceSerial = (device: Device) => {
    if (device.os_type?.toLowerCase().includes('mac')) {
      return `MB${device.serial_number?.substring(0, 3) || 'P14'}-${new Date(device.created_at).getFullYear() || '2023'}-${device.name?.substring(0, 3) || 'XX'}01`;
    } else {
      return device.serial_number || 'N/A';
    }
  };

  const { data: configurations } = useQuery({
    queryKey: ['configurations'],
    queryFn: async () => {
      const { data: session } = await supabase.auth.getSession();
      const { data, error } = await supabase
        .from('configurations')
        .select('*')
        .eq('user_id', session.session?.user.id)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data;
    }
  });

  const triggerHeartbeat = async (deviceId: string) => {
    try {
      const { data: enrollmentToken, error } = await supabase.rpc(
        'get_device_enrollment_token', 
        { device_id: deviceId }
      );

      if (error || !enrollmentToken) {
        toast.error('Device enrollment token not found or access denied');
        return;
      }

      const { error: invokeError } = await supabase.functions.invoke('device-heartbeat', {
        body: {
          device_id: deviceId,
          enrollment_token: enrollmentToken
        }
      });

      if (invokeError) {
        toast.error('Failed to trigger device heartbeat');
        return;
      }

      toast.success('Device heartbeat triggered successfully');
    } catch (error) {
      toast.error('Failed to trigger device heartbeat');
    }
  };

  const openTagAssignment = (device: Device) => {
    setSelectedDeviceForTags({ id: device.id, name: device.name });
    setIsTagAssignmentOpen(true);
  };

  const openTagRemoval = (device: Device) => {
    setSelectedDeviceForRemoval({ id: device.id, name: device.name });
    setIsTagRemovalOpen(true);
  };

  useEffect(() => {
    const fetchTags = async () => {
      const { data, error } = await supabase
        .from("tags")
        .select("*")
        .order("name");
      
      if (!error && data) {
        setAvailableTags(data.map(tag => ({
          id: tag.id,
          name: tag.name,
          description: tag.description || undefined,
          color: tag.color,
          type: tag.type as "static" | "dynamic",
        })));
      }
    };
    
    fetchTags();
  }, []);

  // Compute unique filter options for each column
  const filterOptions = useMemo(() => {
    const statuses = new Set<string>();
    const departments = new Set<string>();
    const locations = new Set<string>();
    const customers = new Set<string>();
    
    devices.forEach(device => {
      const status = device.status || 'pending';
      const department = device.department || '__null__';
      const location = device.location || '__null__';
      const customer = device.customer || '__null__';
      
      statuses.add(status);
      departments.add(department);
      locations.add(location);
      customers.add(customer);
    });
    
    return {
      status: Array.from(statuses).sort().map(s => ({
        value: s,
        label: s.charAt(0).toUpperCase() + s.slice(1),
        icon: <StatusBadge status={s} />
      })),
      department: Array.from(departments).sort().map(d => ({
        value: d,
        label: d === '__null__' ? 'Unassigned' : d,
        icon: (
          <div className="h-5 w-5 rounded-full bg-muted flex items-center justify-center">
            <span className="text-xs text-muted-foreground">
              {d === '__null__' ? 'U' : d.charAt(0)}
            </span>
          </div>
        )
      })),
      location: Array.from(locations).sort().map(l => ({
        value: l,
        label: l === '__null__' ? 'Unknown' : l
      })),
      customer: Array.from(customers).sort().map(c => ({
        value: c,
        label: c === '__null__' ? 'Not Available' : c,
        icon: <CustomerTag customer={c === '__null__' ? 'Not Available' : c} />
      }))
    };
  }, [devices]);

  const columns: ColumnDef<Device>[] = useMemo(
    () => [
      {
        id: "select",
        header: ({ table }) => (
          <Checkbox
            checked={table.getIsAllPageRowsSelected()}
            onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
            aria-label="Select all"
          />
        ),
        cell: ({ row }) => (
          <Checkbox
            checked={row.getIsSelected()}
            onCheckedChange={(value) => row.toggleSelected(!!value)}
            aria-label="Select row"
          />
        ),
        enableSorting: false,
        enableHiding: false,
        size: 50,
      },
      {
        accessorKey: "name",
        header: ({ column }) => (
          <Button
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
            className="h-8 p-0 font-semibold"
          >
            Device Name
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        ),
        cell: ({ row }) => (
          <div 
            className="flex items-center gap-3 cursor-pointer hover:text-primary transition-colors"
            onClick={() => navigate(`/devices/${row.original.id}`)}
          >
            {getDeviceIcon(row.original)}
            <div className="flex-1 min-w-0">
              <div className="font-medium truncate">{row.original.name}</div>
              <div className="text-sm text-muted-foreground truncate">{getDeviceSerial(row.original)}</div>
            </div>
          </div>
        ),
        size: 250,
      },
      {
        id: "tags",
        accessorFn: (row) => {
          const tagList = deviceTags?.get(row.id) || [];
          return tagList.map(t => t.name).join(", ");
        },
        header: ({ column }) => {
          const filterValue = column.getFilterValue() as string[] | undefined;
          const isFiltered = filterValue && filterValue.length > 0;
          
          return (
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
                className="h-8 p-0 font-semibold"
              >
                Tags
                <ArrowUpDown className="ml-2 h-4 w-4" />
              </Button>
              <ColumnTagFilter 
                column={column} 
                availableTags={availableTags}
              />
              {isFiltered && (
                <div 
                  className="h-2 w-2 rounded-full bg-primary" 
                  title={`Filtered by ${filterValue.length} tag(s)`} 
                />
              )}
            </div>
          );
        },
        cell: ({ row }) => {
          const tags = deviceTags?.get(row.original.id) || [];
          
          if (tags.length === 0) {
            return (
              <span className="text-xs text-muted-foreground italic">No tags</span>
            );
          }
          
          return (
            <TagsDisplay 
              resourceId={row.original.id} 
              resourceType="device" 
              size="sm" 
              maxTags={3}
              showRemoveButton={true}
              prefetchedTags={tags}
            />
          );
        },
        filterFn: (row, columnId, filterValue: string[]) => {
          // No filter applied
          if (!filterValue || filterValue.length === 0) {
            return true;
          }
          
          // Get tags for this device
          const rowTags = deviceTags?.get(row.original.id) || [];
          const rowTagIds = rowTags.map(t => t.id);
          
          // OR logic: device has at least one of the selected tags
          return filterValue.some(tagId => rowTagIds.includes(tagId));
        },
        enableColumnFilter: true,
        size: 200,
      },
      {
        accessorKey: "status",
        header: ({ column }) => {
          const filterValue = column.getFilterValue() as string[] | undefined;
          const isFiltered = filterValue && filterValue.length > 0;
          
          return (
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
                className="h-8 p-0 font-semibold"
              >
                Status
                <ArrowUpDown className="ml-2 h-4 w-4" />
              </Button>
              <ColumnMultiSelectFilter
                column={column}
                title="Filter by Status"
                options={filterOptions.status}
              />
              {isFiltered && (
                <div 
                  className="h-2 w-2 rounded-full bg-primary" 
                  title={`Filtered by ${filterValue.length} status(es)`} 
                />
              )}
            </div>
          );
        },
        cell: ({ row }) => <StatusBadge status={row.original.status || 'pending'} />,
        filterFn: (row, columnId, filterValue: string[]) => {
          if (!filterValue || filterValue.length === 0) return true;
          const rowValue = row.original.status || 'pending';
          return filterValue.includes(rowValue);
        },
        enableColumnFilter: true,
        size: 120,
      },
      {
        accessorKey: "department",
        header: ({ column }) => {
          const filterValue = column.getFilterValue() as string[] | undefined;
          const isFiltered = filterValue && filterValue.length > 0;
          
          return (
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
                className="h-8 p-0 font-semibold"
              >
                Department
                <ArrowUpDown className="ml-2 h-4 w-4" />
              </Button>
              <ColumnMultiSelectFilter
                column={column}
                title="Filter by Department"
                options={filterOptions.department}
              />
              {isFiltered && (
                <div 
                  className="h-2 w-2 rounded-full bg-primary" 
                  title={`Filtered by ${filterValue.length} department(s)`} 
                />
              )}
            </div>
          );
        },
        cell: ({ row }) => (
          <div className="flex items-center gap-2">
            <div className="h-5 w-5 rounded-full bg-muted flex items-center justify-center">
              <span className="text-xs text-muted-foreground">{row.original.department?.charAt(0) || 'U'}</span>
            </div>
            <span className="truncate">{row.original.department || 'Unassigned'}</span>
          </div>
        ),
        filterFn: (row, columnId, filterValue: string[]) => {
          if (!filterValue || filterValue.length === 0) return true;
          const rowValue = row.original.department || '__null__';
          return filterValue.includes(rowValue);
        },
        enableColumnFilter: true,
        size: 140,
      },
      {
        accessorKey: "location",
        header: ({ column }) => {
          const filterValue = column.getFilterValue() as string[] | undefined;
          const isFiltered = filterValue && filterValue.length > 0;
          
          return (
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
                className="h-8 p-0 font-semibold"
              >
                Location
                <ArrowUpDown className="ml-2 h-4 w-4" />
              </Button>
              <ColumnMultiSelectFilter
                column={column}
                title="Filter by Location"
                options={filterOptions.location}
              />
              {isFiltered && (
                <div 
                  className="h-2 w-2 rounded-full bg-primary" 
                  title={`Filtered by ${filterValue.length} location(s)`} 
                />
              )}
            </div>
          );
        },
        cell: ({ row }) => <span className="truncate">{row.original.location || 'Unknown'}</span>,
        filterFn: (row, columnId, filterValue: string[]) => {
          if (!filterValue || filterValue.length === 0) return true;
          const rowValue = row.original.location || '__null__';
          return filterValue.includes(rowValue);
        },
        enableColumnFilter: true,
        size: 140,
      },
      {
        accessorKey: "customer",
        header: ({ column }) => {
          const filterValue = column.getFilterValue() as string[] | undefined;
          const isFiltered = filterValue && filterValue.length > 0;
          
          return (
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
                className="h-8 p-0 font-semibold"
              >
                Customer
                <ArrowUpDown className="ml-2 h-4 w-4" />
              </Button>
              <ColumnMultiSelectFilter
                column={column}
                title="Filter by Customer"
                options={filterOptions.customer}
              />
              {isFiltered && (
                <div 
                  className="h-2 w-2 rounded-full bg-primary" 
                  title={`Filtered by ${filterValue.length} customer(s)`} 
                />
              )}
            </div>
          );
        },
        cell: ({ row }) => <CustomerTag customer={row.original.customer || 'Not Available'} />,
        filterFn: (row, columnId, filterValue: string[]) => {
          if (!filterValue || filterValue.length === 0) return true;
          const rowValue = row.original.customer || '__null__';
          return filterValue.includes(rowValue);
        },
        enableColumnFilter: true,
        size: 140,
      },
      {
        accessorKey: "tenantId",
        header: "Tenant ID",
        cell: ({ row }) => <span className="truncate font-mono text-sm">{row.original.tenantId || 'Not Available'}</span>,
        filterFn: "includesString",
        size: 120,
      },
      {
        accessorKey: "last_seen",
        header: ({ column }) => (
          <Button
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
            className="h-8 p-0 font-semibold"
          >
            Last Seen
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        ),
        cell: ({ row }) => (
          <span className="text-sm">
            {getTimeAgo(row.original.last_seen || row.original.created_at)}
          </span>
        ),
        size: 120,
      },
      {
        id: "actions",
        header: () => <div className="text-right">Actions</div>,
        cell: ({ row }) => (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="h-8 w-8">
                <MoreVertical className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuItem onClick={() => navigate(`/devices/${row.original.id}`)}>
                <ExternalLink className="mr-2 h-4 w-4" />
                View Device Details
              </DropdownMenuItem>
              
              <DropdownMenuSeparator />
              <DropdownMenuLabel>Remote Access</DropdownMenuLabel>
              <DropdownMenuItem onClick={() => window.open(`/device/${row.original.id}/terminal`, '_blank')}>
                <Terminal className="mr-2 h-4 w-4" />
                Remote Terminal
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => window.open(`/device/${row.original.id}/remote-control`, '_blank')}>
                <Gamepad2 className="mr-2 h-4 w-4" />
                Remote Control
              </DropdownMenuItem>
              
              <DropdownMenuSeparator />
              <DropdownMenuLabel>Management</DropdownMenuLabel>
              <DropdownMenuItem onSelect={(e) => {
                e.preventDefault();
                openTagAssignment(row.original);
              }}>
                <Tags className="mr-2 h-4 w-4" />
                Assign Tags
              </DropdownMenuItem>
              <DropdownMenuItem onSelect={(e) => {
                e.preventDefault();
                openTagRemoval(row.original);
              }}>
                <X className="mr-2 h-4 w-4" />
                Remove Tags
              </DropdownMenuItem>
              
              <DropdownMenuSeparator />
              <DropdownMenuLabel>System</DropdownMenuLabel>
              <DropdownMenuItem onClick={() => triggerHeartbeat(row.original.id)}>
                <RefreshCw className="mr-2 h-4 w-4" />
                Update Agent
              </DropdownMenuItem>
              <DropdownMenuItem className="text-destructive focus:text-destructive" onClick={() => navigate(`/devices/${row.original.id}`)}>
                <Power className="mr-2 h-4 w-4" />
                Unenroll Device
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        ),
        enableSorting: false,
        enableHiding: false,
        size: 100,
      },
    ],
    [navigate, deviceTags, availableTags, filterOptions]
  );

  const selectedDevices = Object.keys(rowSelection).length > 0 
    ? devices.filter((_, index) => rowSelection[index])
    : [];

  return (
    <div className="space-y-4">
      {/* Bulk Action Toolbar */}
      {selectedDevices.length > 0 && (
        <BulkActionToolbar
          selectedDevices={selectedDevices}
          onClearSelection={() => setRowSelection({})}
          onRefresh={() => {
            // Refresh would typically refetch data
            toast.success(`Refreshing ${selectedDevices.length} devices`);
          }}
        />
      )}

      {/* Table using DataTable component */}
      <DataTable
        data={devices}
        columns={columns}
        enableRowSelection={true}
        enableColumnResizing={true}
        rowSelection={rowSelection}
        onRowSelectionChange={setRowSelection}
        columnVisibility={columnVisibility}
        onColumnVisibilityChange={setColumnVisibility}
        columnFilters={columnFilters}
        onColumnFiltersChange={setColumnFilters}
        sorting={sorting}
        onSortingChange={setSorting}
        columnSizing={columnSizing}
        onColumnSizingChange={setColumnSizing}
        emptyMessage="No devices found."
      />

      {/* Dialogs */}
      {selectedDeviceForTags && (
        <TagAssignmentDialog
          open={isTagAssignmentOpen}
          onOpenChange={setIsTagAssignmentOpen}
          resourceId={selectedDeviceForTags.id}
          resourceType="device"
          resourceName={selectedDeviceForTags.name}
          onAssignmentComplete={() => {
            setSelectedDeviceForTags(null);
          }}
        />
      )}

      {selectedDeviceForRemoval && (
        <TagRemovalDialog
          open={isTagRemovalOpen}
          onOpenChange={setIsTagRemovalOpen}
          deviceId={selectedDeviceForRemoval.id}
          deviceName={selectedDeviceForRemoval.name}
          onRemovalComplete={() => {
            setSelectedDeviceForRemoval(null);
          }}
        />
      )}
    </div>
  );
};

export default DeviceTable;